import React, { useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Input,
  Select,
  List,
  Tag,
  Avatar,
  Tooltip,
  Divider,
  Form,
} from "antd";
import {
  useList,
  useCreate,
  useUpdate,
  useDelete,
  useNavigation,
} from "@refinedev/core";
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  TagOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { Genre } from "@/types";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

export const CategoryList: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<Genre | null>(null);
  const [editingCategory, setEditingCategory] = useState<Genre | null>(null);
  const [form] = Form.useForm();
  const { push } = useNavigation();

  const { mutate: createGenre } = useCreate();
  const { mutate: updateGenre } = useUpdate();
  const { mutate: deleteGenre } = useDelete();

  const {
    data: genresData,
    isLoading,
    refetch,
  } = useList<Genre>({
    resource: "genres",
    pagination: { mode: "off" },
  });

  const handleDelete = (id: number) => {
    deleteGenre({
      resource: "genres",
      id,
      successNotification: () => ({
        message: "Catégorie supprimée",
        description: "La catégorie a été supprimée avec succès",
        type: "success",
      }),
    });
  };

  const handleCreate = (values: any) => {
    createGenre(
      {
        resource: "genres",
        values,
        successNotification: () => ({
          message: "Catégorie créée",
          description: "La catégorie a été créée avec succès",
          type: "success",
        }),
      },
      {
        onSuccess: () => {
          form.resetFields();
          refetch();
        },
      }
    );
  };

  const handleUpdate = (values: any) => {
    if (editingCategory) {
      updateGenre(
        {
          resource: "genres",
          id: editingCategory.id,
          values,
          successNotification: () => ({
            message: "Catégorie mise à jour",
            description: "La catégorie a été mise à jour avec succès",
            type: "success",
          }),
        },
        {
          onSuccess: () => {
            setEditingCategory(null);
            form.resetFields();
            refetch();
          },
        }
      );
    }
  };

  // Mock data
  const mockCategories = [
    {
      id: 1,
      name: "Films",
      description: "Longs métrages et productions cinématographiques",
      slug: "films",
      contentCount: 358,
      status: "ACTIVE",
    },
    {
      id: 2,
      name: "Séries",
      description: "Séries télévisées et productions en épisodes",
      slug: "series",
      contentCount: 245,
      status: "ACTIVE",
    },
    {
      id: 3,
      name: "Documentaires",
      description: "Films documentaires et reportages",
      slug: "documentaires",
      contentCount: 127,
      status: "ACTIVE",
    },
    {
      id: 4,
      name: "Animations",
      description: "Films d'animation pour tous les âges",
      slug: "animations",
      contentCount: 98,
      status: "ACTIVE",
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "green";
      case "INACTIVE":
        return "red";
      default:
        return "default";
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
          Catégories & Genres
        </Title>
        <Text type="secondary">
          Gérez les catégories et genres de votre plateforme
        </Text>
      </div>

      {/* Action Buttons */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col flex="auto">
          <Space>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => push(`/categories/create`)}
            >
              Ajouter une catégorie
            </Button>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<SearchOutlined />}>Exporter</Button>
          </Space>
        </Col>
      </Row>

      {/* Tabs */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={24}>
            <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
              <Button type="primary">Catégories</Button>
              <Button>Genres</Button>
              <Button>Collections</Button>
            </div>
          </Col>
        </Row>
      </Card>

      <Row gutter={[24, 24]}>
        {/* Left Panel - Categories List */}
        <Col xs={24} lg={12}>
          <Card
            title="Catégories principales"
            extra={
              <Space>
                <Button icon={<PlusOutlined />} size="small" />
                <Button icon={<MoreOutlined />} size="small" />
              </Space>
            }
          >
            <Input
              placeholder="Rechercher une catégorie..."
              prefix={<SearchOutlined />}
              style={{ marginBottom: 16 }}
            />

            <List
              dataSource={
                genresData?.data || (mockCategories as unknown as Genre[])
              }
              loading={isLoading}
              renderItem={(category) => (
                <List.Item
                  key={category.id}
                  style={{
                    cursor: "pointer",
                    backgroundColor:
                      selectedCategory?.id === category.id
                        ? "#f0f8ff"
                        : "transparent",
                    padding: "12px 16px",
                    borderRadius: 8,
                    marginBottom: 8,
                    border:
                      selectedCategory?.id === category.id
                        ? "1px solid #1890ff"
                        : "1px solid #f0f0f0",
                  }}
                  onClick={() => setSelectedCategory(category)}
                  actions={[
                    <Tooltip title="Voir" key="view">
                      <Button
                        type="text"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          push(`/categories/show/${category.id}`);
                        }}
                      >
                        Voir
                      </Button>
                    </Tooltip>,
                    <Tooltip title="Modifier" key="edit">
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          push(`/categories/edit/${category.id}`);
                        }}
                      />
                    </Tooltip>,
                    <Tooltip title="Supprimer" key="delete">
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(category.id);
                        }}
                      />
                    </Tooltip>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={<TagOutlined />}
                        style={{
                          backgroundColor:
                            selectedCategory?.id === category.id
                              ? "#1890ff"
                              : "#f0f0f0",
                          color:
                            selectedCategory?.id === category.id
                              ? "white"
                              : "#666",
                        }}
                      />
                    }
                    title={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Text strong>{category.title}</Text>
                        <Tag
                          color={getStatusColor(category.status || "ACTIVE")}
                        >
                          Actif
                        </Tag>
                      </div>
                    }
                    description={
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {category.contentCount} contenus
                        </Text>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* Right Panel - Category Details */}
        <Col xs={24} lg={12}>
          <Card title="Détails de la catégorie">
            {selectedCategory ? (
              <div>
                {editingCategory ? (
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleUpdate}
                    initialValues={editingCategory}
                  >
                    <Form.Item
                      name="name"
                      label="Nom de la catégorie"
                      rules={[{ required: true, message: "Le nom est requis" }]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item name="slug" label="Slug">
                      <Input />
                    </Form.Item>

                    <Form.Item name="description" label="Description">
                      <TextArea rows={4} />
                    </Form.Item>

                    <Form.Item name="parentCategory" label="Catégorie parente">
                      <Select
                        placeholder="Sélectionner une catégorie parente"
                        allowClear
                      >
                        {mockCategories.map((cat) => (
                          <Option key={cat.id} value={cat.id}>
                            {cat.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item>
                      <Space>
                        <Button type="primary" htmlType="submit">
                          Sauvegarder
                        </Button>
                        <Button onClick={() => setEditingCategory(null)}>
                          Annuler
                        </Button>
                      </Space>
                    </Form.Item>
                  </Form>
                ) : (
                  <div>
                    <div style={{ marginBottom: 24 }}>
                      <Title level={4} style={{ margin: 0, marginBottom: 8 }}>
                        {selectedCategory.title}
                      </Title>
                      <Tag
                        color={getStatusColor(
                          selectedCategory.status || "ACTIVE"
                        )}
                      >
                        {selectedCategory.status}
                      </Tag>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                      <Text strong>Contenus associés:</Text>
                      <br />
                      <Text>{selectedCategory.contentCount} contenus</Text>
                    </div>

                    <Divider />

                    <Space>
                      <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => {
                          setEditingCategory(selectedCategory);
                          form.setFieldsValue(selectedCategory);
                        }}
                      >
                        Modifier
                      </Button>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(selectedCategory.id)}
                      >
                        Supprimer
                      </Button>
                    </Space>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <TagOutlined
                  style={{ fontSize: 48, color: "#d9d9d9", marginBottom: 16 }}
                />
                <div>
                  <Text type="secondary">
                    Sélectionnez une catégorie pour voir les détails
                  </Text>
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};
